/**
 * usePmTimeline — the stage-transition recorder's health, plus one page's timeline.
 *
 * Health loads on mount because it answers the question the tab exists for:
 * is each source still writing? The per-page timeline is fetched on demand.
 *
 * Admin-only endpoints: the backend returns 403 for non-admins and this hook
 * propagates the detail string for the page to render.
 */

import { useCallback, useEffect, useState } from 'react';

import { ApiError, pmTimeline } from '@/helpers/ApiHelper';
import type { TaskTimeline, TimelineHealth } from '@/types/pm_stage_event';

interface UsePmTimelineResult {
  health: TimelineHealth | null;
  timeline: TaskTimeline | null;
  isLoading: boolean;
  error: string | null;
  loadTimeline: (notionId: string) => Promise<void>;
  refetch: () => void;
}

export function usePmTimeline(): UsePmTimelineResult {
  const [health, setHealth] = useState<TimelineHealth | null>(null);
  const [timeline, setTimeline] = useState<TaskTimeline | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => {
    setTick((n) => n + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    void (async () => {
      try {
        const h = await pmTimeline.getHealth();
        if (!cancelled) setHealth(h);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof ApiError ? e.detail : 'Failed to load recorder health');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [tick]);

  const loadTimeline = useCallback(async (notionId: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const t = await pmTimeline.getTaskTimeline(notionId);
      setTimeline(t);
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : 'Failed to load task timeline');
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { health, timeline, isLoading, error, loadTimeline, refetch };
}
